/**
 * RFC-0088: Yjs document connection and lifecycle hook.
 * RFC-1023: multi-canvas keep-alive (the warm-pool subscriber behavior below).
 *
 * Connects to the backend WebSocket endpoint and maintains a Yjs document that
 * syncs bidirectionally with the server's pycrdt document.
 *
 * Multi-canvas keep-alive: the actual Y.Doc + WebsocketProvider lifecycle lives
 * in `canvasRoomPool.ts`, a module-scoped warm-connection pool. This hook is a
 * thin subscriber — it `acquireRoom`s the entry for the active canvas and
 * `releaseRoom`s it on switch-away, mirroring the entry's snapshot into React
 * state. Switching between canvases of the same project therefore reuses a warm,
 * already-synced Y.Doc instead of tearing down + cold-loading a fresh one, so
 * the CanvasLoadOverlay progress bar no longer reappears on switch-back.
 *
 * CRITICAL (enforced by the pool): a Y.Doc is never reused across projects.
 * Entries are keyed by `${projectId}/${canvasId}` and other-project entries are
 * evicted on acquire — the Yjs CRDT sync protocol can never send a previous
 * project's state into a new project's room.
 */

import { useEffect, useState } from 'react'
import * as Y from 'yjs'

import {
  acquireRoom,
  releaseRoom,
  readSnapshot,
  subscribeRoom,
  evictIdleProjectRooms,
  type CanvasRoomSnapshot,
  type YDocConnectionError,
} from './canvasRoomPool'

export type { YDocConnectionError } from './canvasRoomPool'

declare global {
  interface Window {
    __ydoc?: Y.Doc
    __ydocProvider?: import('y-websocket').WebsocketProvider
    __Y?: typeof Y
  }
}

interface UseYDocOptions {
  projectId: string
  canvasId?: string
  enabled?: boolean
}

interface UseYDocResult {
  /** The Yjs document (stable identity while the room is warm). */
  ydoc: Y.Doc
  /** Room name the current Y.Doc instance is intended to sync with. */
  roomName: string
  /** Whether the initial sync with the server is complete. */
  synced: boolean
  /** Whether the WebSocket connection is live. */
  connected: boolean
  /** Terminal load error. When set, the provider has stopped reconnecting. */
  error: YDocConnectionError | null
  /**
   * The server's canvas generation, or `null` while unknown — including for the
   * placeholder document below. Undo/redo uses it to refuse history recorded on
   * a previous generation (T289182224).
   */
  roomEpoch: number | null
}

/** Placeholder snapshot used before the room is acquired or when disabled. */
function makePlaceholder(projectId: string, canvasId: string): CanvasRoomSnapshot {
  return {
    ydoc: new Y.Doc(),
    roomName: `${projectId}/${canvasId}`,
    synced: false,
    connected: false,
    error: null,
    // A placeholder has no generation. Null, never 0 — 0 is a real epoch.
    roomEpoch: null,
  }
}

export function useYDoc({
  projectId,
  canvasId = 'agent',
  enabled = true,
}: UseYDocOptions): UseYDocResult {
  // Mirror the pool entry's snapshot into React state. Until the room is
  // acquired (or when disabled) we hand back a disconnected placeholder Y.Doc so
  // consumers always have a valid document to render against.
  const [snapshot, setSnapshot] = useState<CanvasRoomSnapshot>(() =>
    makePlaceholder(projectId, canvasId),
  )

  useEffect(() => {
    if (!enabled || !projectId) {
      // Disabled: present a fresh, disconnected placeholder for the target room.
      setSnapshot(makePlaceholder(projectId, canvasId))
      return
    }

    const entry = acquireRoom(projectId, canvasId)
    const update = () => setSnapshot(readSnapshot(entry))
    const unsubscribe = subscribeRoom(entry, update)
    // Publish the initial snapshot for this entry immediately.
    update()

    return () => {
      unsubscribe()
      releaseRoom(entry.roomName)
    }
  }, [projectId, canvasId, enabled])

  // Project-scoped cleanup: when the workspace unmounts (or the project
  // changes) — but NOT on a same-project canvas switch — evict this project's
  // idle warm rooms so leaving the workspace doesn't leave background canvases
  // holding WebSockets for the full idle window. refCount-gated, so it never
  // touches the still-active room (and is StrictMode-safe).
  useEffect(() => {
    if (!enabled || !projectId) return
    return () => {
      evictIdleProjectRooms(projectId)
    }
  }, [projectId, enabled])

  return {
    ydoc: snapshot.ydoc,
    roomName: snapshot.roomName,
    synced: snapshot.synced,
    connected: snapshot.connected,
    error: snapshot.error,
    roomEpoch: snapshot.roomEpoch,
  }
}
